import { Fragment, type Key, type ReactNode } from "react";
import { handleBananJobComplete, handleKlingJobComplete } from "@/features/ads";
import { ImageSideBySide, VideoSideBySide } from "../cards/SideBySideJobCards";
import type { NormalizedToolPart } from "./chatToolParts";

interface KlingBatchSession {
  jobId: string;
  sessionId: string;
}

function isKlingBatchDispatchOutput(
  output: unknown,
): output is {
  action: "kling_batch_dispatched";
  jobId: string;
  sessions?: KlingBatchSession[];
} {
  if (!output || typeof output !== "object") return false;
  const rec = output as Record<string, unknown>;
  if (rec.action !== "kling_batch_dispatched") return false;
  return typeof rec.jobId === "string";
}

function isBananDispatchOutput(
  output: unknown,
): output is { action: "banan_dispatched"; jobId: string } {
  if (!output || typeof output !== "object") return false;
  const rec = output as Record<string, unknown>;
  return rec.action === "banan_dispatched" && typeof rec.jobId === "string";
}

function isBananBatchDispatchOutput(
  output: unknown,
): output is {
  action: "banan_batch_dispatched";
  jobId: string;
  itemCount?: number;
} {
  if (!output || typeof output !== "object") return false;
  const rec = output as Record<string, unknown>;
  return rec.action === "banan_batch_dispatched" && typeof rec.jobId === "string";
}

interface UgcElementRefsVideo {
  videoIndex: number;
  scriptAngle?: string;
  sessionId?: string;
  jobId?: string;
}

function isUgcElementRefsDispatchOutput(
  output: unknown,
): output is { action: "ugc_element_refs_dispatched"; videos: UgcElementRefsVideo[] } {
  if (!output || typeof output !== "object") return false;
  const rec = output as Record<string, unknown>;
  if (rec.action !== "ugc_element_refs_dispatched") return false;
  if (!Array.isArray(rec.videos)) return false;
  return rec.videos.every(
    (v) => v && typeof v === "object" && typeof (v as Record<string, unknown>).videoIndex === "number",
  );
}

export function renderGeneratedAdJobCards(part: NormalizedToolPart, key: Key): ReactNode | null {
  const output = part.output;
  if (part.state !== "output-available") return null;

  if (isBananBatchDispatchOutput(output)) {
    return (
      <ImageSideBySide
        key={key}
        primary={{ jobId: output.jobId, itemCount: output.itemCount }}
        onComplete={handleBananJobComplete}
      />
    );
  }
  if (isUgcElementRefsDispatchOutput(output)) {
    return (
      <Fragment key={key}>
        {output.videos
          .filter((v): v is UgcElementRefsVideo & { jobId: string } => typeof v.jobId === "string")
          .map((video) => (
            <ImageSideBySide
              key={video.jobId}
              primary={{ jobId: video.jobId }}
              onComplete={handleBananJobComplete}
            />
          ))}
      </Fragment>
    );
  }
  if (isBananDispatchOutput(output)) {
    return (
      <ImageSideBySide
        key={key}
        primary={{ jobId: output.jobId }}
        onComplete={handleBananJobComplete}
      />
    );
  }
  if (isKlingBatchDispatchOutput(output)) {
    const sessions =
      Array.isArray(output.sessions) && output.sessions.length > 0
        ? output.sessions.filter(
            (s): s is KlingBatchSession => !!s && typeof s.jobId === "string",
          )
        : [{ jobId: output.jobId, sessionId: output.jobId }];
    return (
      <Fragment key={key}>
        {sessions.map((s) => (
          <VideoSideBySide
            key={s.jobId}
            primary={{ jobId: s.jobId }}
            onComplete={handleKlingJobComplete}
          />
        ))}
      </Fragment>
    );
  }
  return null;
}
